import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {NgxSpinnerService} from "ngx-spinner";
import Me from "../helpers/me";
import App from "../helpers/app";
import Api from "../helpers/api";
import {take} from "rxjs/operators";
import { saveAs } from "file-saver";
import { BreadcrumbService } from '../shared/services/breadcrumb.service';

interface Filters {
  cidade?: string;
  nome?: string;
  login?: string;
  dataInclusaoDe?: string;
  dataInclusaoAte?: string;
}

/**
 * Componente para gerenciar usuários com acesso remoto por município
 * 
 * Query Params aceitos:
 * - nomeCidade: Nome da cidade para filtro automático (ex: ?nomeCidade=Canoas)
 * 
 * Exemplo de uso:
 * /usuarios-municipio?nomeCidade=Canoas
 */
@Component({
  selector: 'app-usuarios-municipio',
  templateUrl: './usuarios-municipio.component.html',
  styleUrls: ['./usuarios-municipio.component.scss']
})
export class UsuariosMunicipioComponent implements OnInit {
  apiCall = this.api.new().silent();
  
  // Variáveis para paginação
  page = 1;
  pageSize = 10;
  total = 0;
  
  // Variáveis para filtros
  filters: Filters = {
    cidade: null,
    nome: null,
    login: null,
    dataInclusaoDe: null,
    dataInclusaoAte: null
  };
  
  // Variáveis para ordenação
  currentSortColumn: string = '';
  currentSortDirection: string = 'asc';
  
  // Dados
  usuarios = [];
  cidade: string = null;
  
  // Modal
  atualizacaoSenha = {
    novaSenha: null,
    confirmaNovaSenha: null,
  };
  userToUpdate = null;

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private me: Me,
    private app: App,
    private api: Api,
    private breadcrumbService: BreadcrumbService
  ) {
  }

  async ngOnInit() {
    const self = this;
    
    // Configurar breadcrumbs
    self.breadcrumbService.setMunicipioBreadcrumbs('Usuários Município');
    
    // Processar query params
    self.route.queryParams.subscribe(async (parametros) => {
      if (parametros['nomeCidade']) {
        self.cidade = parametros['nomeCidade'];
        self.filters.cidade = parametros['nomeCidade'];
        
        // Carregar dados com a cidade filtrada
        await self.loadUsuarios();
      } else {
        // Se não veio nomeCidade por parâmetro, carregar dados sem filtro
        await self.loadUsuarios();
      }
    });
  }

  async loadUsuarios() {
    const params = this.buildSearchParams();
    
    try {
      this.spinner.show(undefined, {
        type: 'ball-triangle-path',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      });

      const response = await this.apiCall.get('servidor/usuarios/cidade/v2', params).pipe(take(1)).toPromise();
      
      if (response && response.data) {
        this.usuarios = response.data;
        this.total = response.total || response.data.length;
        console.log('Usuários carregados:', response);
      } else if (Array.isArray(response)) {
        // Fallback para caso a API retorne array direto
        this.usuarios = response;
        this.total = response.length;
      }
      
      this.spinner.hide();
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      this.spinner.hide();
      await this.app.alert('Erro!', 'Erro ao carregar usuários. Tente novamente.', 'error');
      this.usuarios = [];
      this.total = 0;
    }
  }

  // Método para buscar dados com indicador de carregamento
  async loadUsuariosWithCurrentFilters() {
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      await this.loadUsuarios();
    } finally {
      this.spinner.hide();
    }
  }

  // Mudança de página
  async onPageChange(page: number) {
    this.page = page;
    await this.loadUsuariosWithCurrentFilters();
  }

  // Construir parâmetros de busca sanitizados
  buildSearchParams(): any {
    const offset = (this.page - 1) * this.pageSize;
    let params: any = {
      offset: offset,
      limit: this.pageSize
    };

    // Sanitizar e adicionar filtros apenas se tiverem valores
    if (this.filters.cidade && this.filters.cidade.trim() !== '') {
      params.cidade = this.filters.cidade.trim();
    }

    // Combinar filtros de nome e login em um único parâmetro user
    let userParam = '';
    if (this.filters.nome && this.filters.nome.trim() !== '') {
      userParam = this.filters.nome.trim();
    }
    if (this.filters.login && this.filters.login.trim() !== '') {
      userParam = this.filters.login.trim();
    }
    if (userParam) {
      params.user = userParam;
    }

    // Filtros de data
    if (this.filters.dataInclusaoDe && this.filters.dataInclusaoDe.trim() !== '') {
      const dataFormatada = this.convertDateFormat(this.filters.dataInclusaoDe);
      if (dataFormatada) {
        params.data_inclusao_de = dataFormatada;
      }
    }

    if (this.filters.dataInclusaoAte && this.filters.dataInclusaoAte.trim() !== '') {
      const dataFormatada = this.convertDateFormat(this.filters.dataInclusaoAte);
      if (dataFormatada) {
        params.data_inclusao_ate = dataFormatada;
      }
    }

    // Ordenação
    if (this.currentSortColumn && this.currentSortColumn.trim() !== '') {
      params.order_by = this.currentSortColumn;
      params.order_direction = this.currentSortDirection.toUpperCase();
    }

    console.log('Parâmetros de busca:', params);
    return params;
  }

  // Converter formato de data de dd/mm/yyyy para yyyy-mm-dd
  convertDateFormat(dateString: string): string | null {
    if (!dateString) return null;
    
    // Converter de dd/mm/yyyy para yyyy-mm-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  // Aplicar filtros
  applyFilters() {
    console.log('Aplicando filtros:', this.filters);
    this.page = 1; // Reset para primeira página
    this.loadUsuariosWithCurrentFilters();
  }

  // Limpar filtros
  clearFilters() {
    // Preservar nomeCidade se veio por parâmetro
    const cidadeOriginal = this.cidade;
    
    this.filters = {
      cidade: cidadeOriginal,
      nome: null,
      login: null,
      dataInclusaoDe: null,
      dataInclusaoAte: null
    };
    
    this.page = 1;
    this.currentSortColumn = '';
    this.currentSortDirection = 'asc';
    this.loadUsuariosWithCurrentFilters();
  }

  // Método para ordenação das colunas
  sortTable(column: string) {
    // Mapear colunas do frontend para backend
    const columnMap = {
      'municipio': 'c.nome',
      'nome': 'u.login',
      'login': 'u.login',
      'data_inclusao': 'u.data_inclusao'
    };

    const mappedColumn = columnMap[column] || column;

    // Verificar se é a mesma coluna usando o valor mapeado
    if (this.currentSortColumn === mappedColumn) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = mappedColumn;
      this.currentSortDirection = 'asc';
    }

    this.page = 1; // Reset para primeira página
    console.log(`Ordenando por: ${this.currentSortColumn} ${this.currentSortDirection}`);
    this.loadUsuariosWithCurrentFilters();
  }

  // Método para mostrar indicador de ordenação
  getSortIcon(column: string): string {
    const columnMap = {
      'municipio': 'c.nome',
      'nome': 'u.login',
      'login': 'u.login',
      'data_inclusao': 'u.data_inclusao'
    };

    if (this.currentSortColumn === columnMap[column]) {
      return this.currentSortDirection === 'asc' ? 'fa-caret-up' : 'fa-caret-down';
    }
    return '';
  }

  // Método para verificar se a coluna está sendo ordenada
  isColumnSorted(column: string): boolean {
    const columnMap = {
      'municipio': 'c.nome',
      'nome': 'u.login',
      'login': 'u.login',
      'data_inclusao': 'u.data_inclusao'
    };
    return this.currentSortColumn === columnMap[column];
  }

  async download(chave, nome){
    const url = await this.apiCall.get('banco-dados/files/url').pipe(take(1)).toPromise()
    if(url) saveAs(url+chave, nome);
  }

  async selecionaArquivo(event) {
    const self = this
    if(self.cidade){
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        console.log(file)
        if(file.name.endsWith('xls') || file.name.endsWith('xlsx')){
          const formData = new FormData();
          formData.append('file', file);
          try{
            self.spinner.show(undefined,
              {
                type: 'ball-triangle-path',
                size: 'medium',
                bdColor: 'rgba(0, 0, 0, 0.8)',
                color: '#fff',
                fullScreen: true
              });

            const uploadFiles = await this.apiCall.post('servidor/arquivo/usuarios?cidade='+self.cidade, formData).pipe(take(1)).toPromise()
            self.spinner.hide()
            await this.app.alert('Tudo certo!', 'Usuários importados com sucesso!', 'success')
            await self.loadUsuarios(); // Recarregar dados após upload
          }catch(e){
            self.spinner.hide();
            await self.loadUsuarios(); // Recarregar dados mesmo em caso de erro
          }
        } else {
          await this.app.alert('Erro!', 'Por favor, selecione um arquivo Excel (.xls ou .xlsx)', 'error');
        }
      }
    } else {
      await this.app.alert('Atenção!', 'É necessário selecionar uma cidade primeiro.', 'warning');
    }
  }

  async updateSenha(modal){
    if(!this.userToUpdate.id || !this.atualizacaoSenha.novaSenha) return
    this.spinner.show(undefined,
      {
        type: 'ball-triangle-path',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      });
    await this.apiCall.get(`servidor/usuarios/cidade/senha?id=${this.userToUpdate.id}&password=${this.atualizacaoSenha.novaSenha}`).pipe(take(1)).toPromise()
    this.spinner.hide()
    await this.app.alert('Tudo certo!', 'Senha atualizada com sucesso!', 'success')
    modal.close('Close click')
    await this.loadUsuarios(); // Recarregar dados após atualização
  }

  openModal(content, usuario){
    this.userToUpdate = usuario
    this.atualizacaoSenha = {
      novaSenha: null,
      confirmaNovaSenha: null,
    };
    const modal = this.modalService.open(content, { centered: true })
  }

  openUploadModal(content) {
    const modal = this.modalService.open(content, { centered: true });
  }

  async excluirUsuario(usuario) {
    const confirmResult = await this.app.confirm(
      'Confirmar Exclusão',
      `Tem certeza que deseja excluir o usuário "${usuario.user}"?`,
      'warning'
    );

    if (confirmResult) {
      try {
        this.spinner.show(undefined, {
          type: 'ball-triangle-path',
          size: 'medium',
          bdColor: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          fullScreen: true
        });

        await this.apiCall.delete(`servidor/usuarios/cidade/${usuario.id}`).pipe(take(1)).toPromise();
        
        this.spinner.hide();
        await this.app.alert('Sucesso!', 'Usuário excluído com sucesso!', 'success');
        await this.loadUsuarios(); // Recarregar dados após exclusão
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
        this.spinner.hide();
        await this.app.alert('Erro!', 'Erro ao excluir usuário. Tente novamente.', 'error');
      }
    }
  }
}
